import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useBefundBogen } from "../../context/BefundbogenContext";
import axios from "axios";
import styles from "./DiabetesBefundForm.module.css";


function addUnique(arr, value) {
  if (value === undefined || value === null || value === "") return arr;
  return arr.includes(value) ? arr : [...arr, value];
}
function removeValue(arr, value) {
  return arr.filter((x) => x !== value);
}

export default function DiabetesBefundForm() {
  const { befundData, setBefundData } = useBefundBogen();
  // left/right hold symptoms[] and criteria[]
  const [left, setLeft] = useState({ symptoms: [], criteria: [] });
  const [right, setRight] = useState({ symptoms: [], criteria: [] });

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Rehydrate form from Context (when coming back from Diagnose)
useEffect(() => {
  if (befundData?.form) {
    setLeft({
      symptoms: befundData.form.symptoms_left || [],
      criteria: befundData.form.criteria_left || [],
    });

    setRight({
      symptoms: befundData.form.symptoms_right || [],
      criteria: befundData.form.criteria_right || [],
    });
  }
}, [befundData]);


  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  // Generic toggles
function toggleSymptom(side, value, checked) {
  const setter = side === "left" ? setLeft : setRight;

  setter((prev) => {
    let symptoms = checked ? addUnique(prev.symptoms, value) : removeValue(prev.symptoms, value);
    let criteria = prev.criteria.slice();

    // Special logic if toggling PNP
    if (value === "pnp" && !checked) {
      // Remove Ulcus Akut and its children
      symptoms = symptoms.filter(s => s !== "ulcus akut" && s !== "ausser Ferse" && s !== "Ferse");
      criteria = criteria.filter(c =>
        ![
          "Nicht plantare Laesion",
          "ulcus mit plantarer Laesion",
          "milde Infektion",
          "moderate Infektion",
          "starke Infektion",
          "starke Exsudation"
        ].includes(c)
      );

      // Remove CNO and its children
      symptoms = symptoms.filter(s => s !== "CNO" && s !== "CNO aktiv" && s !== "CNO inaktiv");
      criteria = criteria.filter(c =>
        ![
          "Vorfuss",
          "Mittelfuss",
          "Rueckfuss",
          "minderdeformiert mit erhaltener Belastbarkeit",
          "mit Derformation",
          "Gelenkdestruktion USG / OSG starke Lotabweichung",
          "Mobilitaetsgrad 1"
        ].includes(c)
      );
    }

    return { ...prev, symptoms, criteria };
  });
}

function toggleUlcusAbgeheilt(side, checked) {
  const setter = side === "left" ? setLeft : setRight;

  setter(prev => {
    let symptoms = [...prev.symptoms];
    let criteria = [...prev.criteria];

    if (checked) {
      // Add abgeheilt
      symptoms = addUnique(symptoms, "ulcus abgeheilt");

      // Remove akut + all akut children
      const akutChildSymptoms = ["ausser Ferse", "Ferse"];
      const akutChildCriteria = [
        "Nicht plantare Laesion",
        "ulcus mit plantarer Laesion",
        "milde Infektion",
        "moderate Infektion",
        "starke Infektion",
        "starke Exsudation"
      ];

      symptoms = symptoms.filter(
        s => s !== "ulcus akut" && !akutChildSymptoms.includes(s)
      );
      criteria = criteria.filter(
        c => !akutChildCriteria.includes(c)
      );
    } else {
      symptoms = removeValue(symptoms, "ulcus abgeheilt");
    }

    return { ...prev, symptoms, criteria };
  });
}



  function toggleCriteria(side, value, checked) {
    const setter = side === "left" ? setLeft : setRight;
    setter((prev) => ({
      ...prev,
      criteria: checked ? addUnique(prev.criteria, value) : removeValue(prev.criteria, value),
    }));
  }

  // SECTION 2 pAVK
  function togglePavkTop(side, checked) {
    const setter = side === "left" ? setLeft : setRight;
    setter((prev) => {
      let symptoms = prev.symptoms.slice();
      let criteria = prev.criteria.filter(
        (v) => !["milde pavk", "moderate pavk", "ausgepraegte pavk"].includes(v)
      );

      if (!checked) {
        symptoms = removeValue(symptoms, "pavk");
      } else {
        symptoms = addUnique(symptoms, "pavk");
      }

      return { ...prev, symptoms, criteria };
    });
  }

  function choosePavkType(side, type, checked) {
    const setter = side === "left" ? setLeft : setRight;
    setter((prev) => {
      let criteria = prev.criteria.filter(
        (v) => !["milde pavk", "moderate pavk", "ausgepraegte pavk"].includes(v)
      );
      let symptoms = prev.symptoms.slice();

      if (checked) {
        const mapping = {
          "milde pavk": "milde pavk",
          "moderate pavk": "moderate pavk",
          "ausgepraegte pavk": "ausgepraegte pavk",
        };
        criteria = addUnique(criteria, mapping[type]);
        symptoms = addUnique(symptoms, "pavk");
      } else {
        symptoms = removeValue(symptoms, "pavk");
      }

      return { ...prev, symptoms, criteria };
    });
  }

  function toggleUlcusAkut(side, checked) {
    const setter = side === "left" ? setLeft : setRight;

    const childSymptoms = ["ausser Ferse", "Ferse"];
    const childCriteria = [
      "Nicht plantare Laesion",
      "ulcus mit plantarer Laesion",
      "milde Infektion",
      "moderate Infektion",
      "starke Infektion",
      "starke Exsudation",
    ];

    setter(prev => {
      let symptoms = [...prev.symptoms];
      let criteria = [...prev.criteria];

      if (checked) {
        // Add akut
        symptoms = addUnique(symptoms, "ulcus akut");

        // Remove abgeheilt
        symptoms = removeValue(symptoms, "ulcus abgeheilt");
      } else {
        // Remove akut
        symptoms = removeValue(symptoms, "ulcus akut");

        // Remove akut children
        symptoms = symptoms.filter(s => !childSymptoms.includes(s));
        criteria = criteria.filter(c => !childCriteria.includes(c));
      }

      return { ...prev, symptoms, criteria };
    });  
  }

  // 1) Location type (Nicht plantare / plantar) - exclusive
function chooseUlcusLocationType(side, choice) {
  const setter = side === "left" ? setLeft : setRight;
  setter((prev) => {
    const filtered = prev.criteria.filter(
      (c) => !["Nicht plantare Laesion", "ulcus mit plantarer Laesion"].includes(c)
    );
    if (choice) filtered.push(choice);
    return { ...prev, criteria: filtered };
  });
}

// 2) Heel involvement (außer Ferse / Ferse) - exclusive
function chooseUlcusHeel(side, choice) {
  const setter = side === "left" ? setLeft : setRight;
  setter((prev) => {
    const filtered = prev.symptoms.filter(
      (s) => !["ausser Ferse", "Ferse"].includes(s)
    );
    if (choice) filtered.push(choice);
    return { ...prev, symptoms: filtered };
  });
}

// 3) Infection / Exudation (exclusive)
function chooseUlcusInfection(side, choice) {
  const setter = side === "left" ? setLeft : setRight;
  setter((prev) => {
    const filtered = prev.criteria.filter(
      (c) => !["milde Infektion", "moderate Infektion", "starke Infektion", "starke Exsudation"].includes(c)
    );
    if (choice) filtered.push(choice);
    return { ...prev, criteria: filtered };
  });
}

  // SECTION 6 Deformities top-level "Ja" toggles showing nested (multiple allowed)
  function toggleDeformTop(side, checked) {
    const setter = side === "left" ? setLeft : setRight;
    setter((prev) => {
      const symptoms = checked ? addUnique(prev.symptoms, "Deformitaet") : removeValue(prev.symptoms, "Deformitaet");
      // if disabling, remove related deform criteria
      const criteria = checked
        ? prev.criteria
        : prev.criteria.filter(
            (v) =>
              v !== "ausgepraegte Hohlfuss" &&
              v !== "ausgepraegte Knick/Senkfuss" &&
              v !== "ausgepraegte Zehenfehlstellung" &&
              v !== "ausgepraegte Dyspropertion, Deformitaeten bzw. Dysproportionen, nicht nach konfektioniertem Leisten"
          );
      return { ...prev, symptoms, criteria };
    });
  }

  // SECTION 7 Amputations: top-level "Ja" shows Zeh and Teil blocks
  function toggleAmputationTop(side, checked) {
    const setter = side === "left" ? setLeft : setRight;
    setter((prev) => {
      const symptoms = checked ? addUnique(prev.symptoms, "Amputation") : removeValue(prev.symptoms, "Amputation");
      let criteria = prev.criteria.slice();
      if (!checked) {
        // remove all amputation-related criteria & symptoms
        criteria = criteria.filter(
          (v) =>
            !v.startsWith("Zeh") &&
            v !== "Transmetatarsale Teilamputation des Vorfusses" &&
            v !== "Teil Amputation Mittelfuss" &&
            v !== "Teil Amputation Rueckfuss" &&
            v !== "belaster Fussstumpf" &&
            v !== "nicht belaster Fussstumpf"
        );
        // also remove specific Zeh Amputation symptom if present
        const symptoms2 = symptoms.filter((s) => s !== "Zeh Amputation" && s !== "Teil Amputation");
        return { ...prev, symptoms: symptoms2, criteria };
      }
      return { ...prev, symptoms, criteria };
    });
  }

  // Zeh Amputation checkbox toggles symptom and its D1..D5 children
  function toggleZehAmputation(side, checked) {
    const setter = side === "left" ? setLeft : setRight;
    setter((prev) => {
      let symptoms = checked ? addUnique(prev.symptoms, "Zeh Amputation") : removeValue(prev.symptoms, "Zeh Amputation");
      let criteria = prev.criteria.slice();
      if (!checked) {
        // remove D1..D5
        criteria = criteria.filter(
          (v) => !["Zeh D1 Amputation", "Zeh D2 Amputation", "Zeh D3 Amputation", "Zeh D4 Amputation", "Zeh D5 Amputation"].includes(v)
        );
      }
      return { ...prev, symptoms, criteria };
    });
  }
  function toggleZehDigit(side, digit, checked) {
    const criterion = `Zeh ${digit} Amputation`;
    toggleCriteria(side, criterion, checked);
  }

  // Teil Amputation: top-level checkbox toggles "Teil Amputation" symptom
  function toggleTeilAmputationTop(side, checked) {
    const setter = side === "left" ? setLeft : setRight;
    setter((prev) => {
      let symptoms = checked ? addUnique(prev.symptoms, "Teil Amputation") : removeValue(prev.symptoms, "Teil Amputation");
      let criteria = prev.criteria.slice();
      if (!checked) {
        // remove both groups criteria
        criteria = criteria.filter(
          (v) =>
            v !== "Transmetatarsale Teilamputation des Vorfusses" &&
            v !== "Teil Amputation Mittelfuss" &&
            v !== "Teil Amputation Rueckfuss" &&
            v !== "belaster Fussstumpf" &&
            v !== "nicht belaster Fussstumpf"
        );
      }
      return { ...prev, symptoms, criteria };
    });
  }
function chooseTeilGroupA(side, choice) {
  const setter = side === "left" ? setLeft : setRight;
  setter((prev) => {
    let criteria = prev.criteria.filter(
      (v) => !["Transmetatarsale Teilamputation des Vorfusses", "Teil Amputation Mittelfuss", "Teil Amputation Rueckfuss"].includes(v)
    );

    let symptoms = prev.symptoms.slice();

    if (choice) {
      criteria = addUnique(criteria, choice);

      // If Mittelfuß or Rückfuß is selected
      if (choice === "Teil Amputation Mittelfuss" || choice === "Teil Amputation Rueckfuss") {
        // remove Zeh Amputation and its children
        symptoms = symptoms.filter((s) => s !== "Zeh Amputation");
        criteria = criteria.filter(
          (c) =>
            !["Zeh D1 Amputation", "Zeh D2 Amputation", "Zeh D3 Amputation", "Zeh D4 Amputation", "Zeh D5 Amputation"].includes(c)
        );
      }
    }

    return { ...prev, criteria, symptoms };
  });
}
  function chooseTeilGroupB(side, choice) {
    // choice is one of the two or empty
    const setter = side === "left" ? setLeft : setRight;
    setter((prev) => {
      let criteria = prev.criteria.filter((v) => !["belaster Fussstumpf", "nicht belaster Fussstumpf"].includes(v));
      if (choice) criteria = addUnique(criteria, choice);
      return { ...prev, criteria };
    });
  }

  function toggleCnoTop(side, checked) {
    // top-level "CNO Ja" just toggles presence of symptom "CNO"
    const setter = side === "left" ? setLeft : setRight;
    setter((prev) => {
      const symptoms = checked ? addUnique(prev.symptoms, "CNO") : removeValue(prev.symptoms, "CNO");
      if (!checked) {
        // remove all cno-related child symptoms/criteria
        const criteria = prev.criteria.filter(
          (v) =>
            ![
              "Vorfuss",
              "Mittelfuss",
              "Rueckfuss",
              "minderdeformiert mit erhaltener Belastbarkeit",
              "mit Derformation",
              "Gelenkdestruktion USG / OSG starke Lotabweichung",
              "Mobilitaetsgrad 1",
            ].includes(v)
        );
        const symptoms2 = symptoms.filter((s) => s !== "CNO inaktiv" && s !== "CNO aktiv");
        return { ...prev, symptoms: symptoms2, criteria };
      }
      return { ...prev, symptoms };
    });
  }

function toggleCnoMode(side, mode, checked) {
  const setter = side === "left" ? setLeft : setRight;

  const thisSymptom = mode === "inaktiv" ? "CNO inaktiv" : "CNO aktiv";
  const otherSymptom = mode === "inaktiv" ? "CNO aktiv" : "CNO inaktiv";

  const children = [
    "Vorfuss",
    "Mittelfuss",
    "Rueckfuss",
    "minderdeformiert mit erhaltener Belastbarkeit",
    "mit Derformation",
    "Gelenkdestruktion USG / OSG starke Lotabweichung",
    "Mobilitaetsgrad 1",
  ];

  setter((prev) => {
    let symptoms = prev.symptoms.slice();
    let criteria = prev.criteria.slice();

    if (checked) {
      symptoms = symptoms.filter((s) => s !== otherSymptom);
      symptoms = addUnique(symptoms, thisSymptom);

      criteria = criteria.filter((c) => !children.includes(c));
    } else {
      symptoms = removeValue(symptoms, thisSymptom);
      criteria = criteria.filter((c) => !children.includes(c));
    }

    return { ...prev, symptoms, criteria };
  });
}

  function chooseCnoLocation(side, mode, location) {
    // exclusive among Vorfuss, Mittelfuss, Rueckfuss
    const setter = side === "left" ? setLeft : setRight;
    setter((prev) => {
      const filtered = prev.criteria.filter((v) => !["Vorfuss", "Mittelfuss", "Rueckfuss"].includes(v));
      if (!location) return { ...prev, criteria: filtered };
      return { ...prev, criteria: addUnique(filtered, location) };
    });
  }
  function chooseCnoDeform(side, mode, deform) {
    // exclusive between minderdeformiert... and mit Derformation
    const setter = side === "left" ? setLeft : setRight;
    setter((prev) => {
      const filtered = prev.criteria.filter(
        (v) => v !== "minderdeformiert mit erhaltener Belastbarkeit" && v !== "mit Derformation"
      );
      if (!deform) return { ...prev, criteria: filtered };
      return { ...prev, criteria: addUnique(filtered, deform) };
    });
  }
  function toggleCnoExtra(side, mode, key, checked) {
    const map = {
      destr: "Gelenkdestruktion USG / OSG starke Lotabweichung",
      mobi: "Mobilitaetsgrad 1",
    };
    toggleCriteria(side, map[key], checked);
  }

  // SECTION 9 Dialyse
  function toggleDialyse(side, checked) {
    toggleCriteria(side, "dialysepflichtig", checked);
  }

  // Helpers to check presence
  const leftHasSymptom = (val) => left.symptoms.includes(val);
  const rightHasSymptom = (val) => right.symptoms.includes(val);
  const leftHasCriteria = (val) => left.criteria.includes(val);
  const rightHasCriteria = (val) => right.criteria.includes(val);

  // Build payload
  function buildPayload() {
    return {
      symptoms_left: left.symptoms,
      symptoms_right: right.symptoms,
      criteria_left: left.criteria,
      criteria_right: right.criteria,
    };
  }

async function handleSubmit(e) {
  e.preventDefault();

  setLoading(true);
  setError(null);

  const payload = buildPayload();

  setBefundData({ form: payload });

  try {
    const stored = JSON.parse(localStorage.getItem("AUTH_DATA") || "{}");
    const token = stored?.jwt;

    const resp = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/risk/evaluate`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    setBefundData({
      form: payload,
      result: resp.data,
    });

    // redirect to Diagnose page WITH result
    navigate("/bewertung", { state: { result: resp.data } });

  } catch (err) {
    setError(err.response?.data || err.message || String(err));
  } finally {
    setLoading(false);
  }
}

// EXCLUSIVE DEFORMITY HANDLER FOR SECTION 6
function chooseExclusiveDeformity(side, choice, checked) {
  const exclusive = ["ausgepraegte Hohlfuss", "ausgepraegte Knick/Senkfuss"];
  const setter = side === "left" ? setLeft : setRight;

  setter((prev) => {
    let criteria = prev.criteria.slice();

    // Remove both exclusive deformities before applying a new one
    criteria = criteria.filter((c) => !exclusive.includes(c));

    // Add selected deformity (if checked)
    if (checked) {
      if (!criteria.includes(choice)) criteria.push(choice);
    }

    return { ...prev, criteria };
  });
}

  return (
  <div className={styles["befund-container"]}>
    <h1> Rezeptvorschlag (für Ärzte)</h1>
    <p className={styles.muted}>
      Das Ergebnis basiert auf den{" "}
      <a
        href="https://ag-fuss-ddg.de/fileadmin/user_upload/Risikogruppeneinteilung_zur_PG31.pdf"
        target="_blank"
        rel="noopener noreferrer"
      >
        Leitlinien der DDG Arbeitsgemeinschaft Diabetischer Fußleitlinien
      </a>
      , aber es benötigt immer die ärztliche Expertise.
    </p>
    <form onSubmit={handleSubmit}>
      <table className={styles["befund-table"]}>
        <thead>
          <tr>
            <th className={styles["col-clinical"]}>Klinischer Befund</th>
            <th className={styles["col-left"]}>linker Fuß</th>
            <th className={styles["col-right"]}>rechter Fuß</th>
          </tr>
        </thead>
        <tbody>
          {/* 1) PNP */}
          <tr>
            <td className={styles.clinical}>
              1) Hat der/die Patient*in eine{" "}
              <span className={styles["hover-term"]}>
                periphere Polyneuropathie (PNP)
                <span className={styles.tooltip}>
                  <strong>Periphere Polyneuropathie (PNP)</strong>
                  <br /><br />
                  Diabetische Neuropathie: Das Vorhandensein von Symptomen oder Anzeichen
                  für eine Nervenfunktionsstörung bei Personen mit einer Diabetes-(Vor-)Erkrankung,
                  nach Ausschluss anderer Ursachen.<br /><br />
                  Eine periphere Neuropathie kann mit dem 10g-(5,07 Semmes-Weinstein)-Monofilament
                  (Erkennung von Verlust des Schutzgefühls) und einer Stimmgabel (128 Hz,
                  Erkennung von Verlust der Vibrationsempfindung) festgestellt werden.<br /><br />
                  <em>Sybille Wunderlich. IWGDF Guidelines (2019), S.24, S.203</em>
                </span>
              </span>
              ?
            </td>
            <td className={styles.cell}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>linker Fuß</div>
              <label className={styles.inline}>
                <input
                  type="checkbox"
                  checked={leftHasSymptom("pnp")}
                  onChange={(e) => toggleSymptom("left", "pnp", e.target.checked)}
                /> Ja
              </label>
            </td>
            <td className={styles.cell}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>rechter Fuß</div>
              <label className={styles.inline}>
                <input
                  type="checkbox"
                  checked={rightHasSymptom("pnp")}
                  onChange={(e) => toggleSymptom("right", "pnp", e.target.checked)}
                /> Ja
              </label>
            </td>
          </tr>

          {/* 2) pAVK */}
          <tr>
            <td className={styles.clinical}>
              2) Hat der/die Patient*in eine{" "}
              <span className={styles["hover-term"]}>
                periphere arterielle Verschlusskrankheit (pAVK)
                <span className={styles.tooltip}>
                  <strong>pAVK</strong><br /><br />
                  <strong>Periphere arterielle Verschlusskrankheit (pAVK):</strong>
                  Atherosklerotische Gefäßerkrankungen mit klinischen Symptomen
                  und/oder pathologischen Befunden oder Anomalien bei nicht invasiver
                  Gefäßuntersuchung, mit der Folge einer gestörten oder verminderten
                  Durchblutung in mindestens einer Extremität.
                  <br /><br />
                  <strong>Untersuchen Sie:</strong><br />
                  • Puls am Fuß<br />
                  • Knöchel-Arm-Index (ABI)<br />
                  • Duplex-Sonografie oder Angiografie
                  <br /><br />
                  <em>
                    Sybille Wunderlich. »IWGDF Guidelines on the prevention and management
                    of diabetic foot disease«. In: (2019) S.203
                  </em>
                </span>
              </span>
              ?
            </td>
            <td className={styles.cell}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>linker Fuß</div>
              <div>
                <label className={styles.inline}>
                  <input
                    type="checkbox"
                    checked={left.symptoms.includes("pavk")}
                    onChange={(e) => togglePavkTop("left", e.target.checked)}
                  /> Ja
                </label>
                {left.symptoms.includes("pavk") && (
                  <div style={{ marginLeft: 12, marginTop: 6 }}>
                    <div className={styles.small}>1 auswählen</div>
                    <div className={styles.radios}>
                      <label>
                        <input
                          type="checkbox"
                          checked={leftHasCriteria("milde pavk")}
                          onChange={(e) => choosePavkType("left", "milde pavk", e.target.checked)}
                        /> milde pAVK
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={leftHasCriteria("moderate pavk")}
                          onChange={(e) => choosePavkType("left", "moderate pavk", e.target.checked)}
                        /> moderate pAVK
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={leftHasCriteria("ausgepraegte pavk")}
                          onChange={(e) => choosePavkType("left", "ausgepraegte pavk", e.target.checked)}
                        /> ausgeprägte pAVK
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </td>
            <td className={styles.cell}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>rechter Fuß</div>
              <div>
                <label className={styles.inline}>
                  <input
                    type="checkbox"
                    checked={right.symptoms.includes("pavk")}
                    onChange={(e) => togglePavkTop("right", e.target.checked)}
                  /> Ja
                </label>
                {right.symptoms.includes("pavk") && (
                  <div style={{ marginLeft: 12, marginTop: 6 }}>
                    <div className={styles.small}>1 auswählen</div>
                    <div className={styles.radios}>
                      <label>
                        <input
                          type="checkbox"
                          checked={rightHasCriteria("milde pavk")}
                          onChange={(e) => choosePavkType("right", "milde pavk", e.target.checked)}
                        /> milde pAVK
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={rightHasCriteria("moderate pavk")}
                          onChange={(e) => choosePavkType("right", "moderate pavk", e.target.checked)}
                        /> moderate pAVK
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={rightHasCriteria("ausgepraegte pavk")}
                          onChange={(e) => choosePavkType("right", "ausgepraegte pavk", e.target.checked)}
                        /> ausgeprägte pAVK
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </td>
          </tr>

          {/* 3) Ulcus / Läsion */}
          <tr>
            <td className={styles.clinical}>3) Hat der/die Patient*in ein Ulcus bzw. eine Läsion?</td>
            <td className={styles.cell}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>linker Fuß</div>
              <div>
                <label className={styles.inline}>
                  <input
                    type="checkbox"
                    checked={leftHasSymptom("ulcus abgeheilt")}
                    onChange={(e) => toggleUlcusAbgeheilt("left", e.target.checked)}
                  /> abgeheilt
                </label>
                <label className={styles.inline}>
                  <input
                    type="checkbox"
                    checked={leftHasSymptom("ulcus akut")}
                    onChange={(e) => toggleUlcusAkut("left", e.target.checked)}
                    disabled={!leftHasSymptom("pnp")}
                  /> Ulcus akut
                </label>
                {leftHasSymptom("ulcus akut") && (
                  <div className={styles.checkboxes} style={{ marginLeft: 12 }}>
                    {/* 1) Location type */}
                    <div className={styles.small}>1 auswählen</div>
                    <div className={styles.radios}>
                      <label>
                        <input
                          type="checkbox"
                          checked={leftHasCriteria("Nicht plantare Laesion")}
                          onChange={(e) => chooseUlcusLocationType("left", e.target.checked ? "Nicht plantare Laesion" : "")}
                        />
                        Nicht plantare Läsionen
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={leftHasCriteria("ulcus mit plantarer Laesion")}
                          onChange={(e) => chooseUlcusLocationType("left", e.target.checked ? "ulcus mit plantarer Laesion" : "")}
                        />
                        Ulcus mit plantarer Laesion
                      </label>
                    </div>
                    {/* 2) Heel involvement */}
                    <div className={styles.small} style={{ marginTop: 8 }}>1 auswählen</div>
                    <div className={styles.radios}>
                      <label>
                        <input
                          type="checkbox"
                          checked={leftHasSymptom("ausser Ferse")}
                          onChange={(e) => chooseUlcusHeel("left", e.target.checked ? "ausser Ferse" : "")}
                        />
                        außer Ferse
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={leftHasSymptom("Ferse")}
                          onChange={(e) => chooseUlcusHeel("left", e.target.checked ? "Ferse" : "")}
                        />
                        Ferse
                      </label>
                    </div>
                    {/* 3) Infection / Exudation */}
                    <div className={styles.small} style={{ marginTop: 8 }}>1 auswählen</div>
                    <div className={styles.radios}>
                      <label>
                        <input
                          type="checkbox"
                          checked={leftHasCriteria("milde Infektion")}
                          onChange={(e) => chooseUlcusInfection("left", e.target.checked ? "milde Infektion" : "")}
                        />
                        Ulcus milde Infektion
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={leftHasCriteria("moderate Infektion")}
                          onChange={(e) => chooseUlcusInfection("left", e.target.checked ? "moderate Infektion" : "")}
                        />
                        Ulcus moderate Infektion
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={leftHasCriteria("starke Infektion")}
                          onChange={(e) => chooseUlcusInfection("left", e.target.checked ? "starke Infektion" : "")}
                        />
                        Ulcus starke Infektion
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={leftHasCriteria("starke Exsudation")}
                          onChange={(e) => chooseUlcusInfection("left", e.target.checked ? "starke Exsudation" : "")}
                        />
                        Ulcus starke Exsudation
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </td>
            <td className={styles.cell}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>rechter Fuß</div>
              <div>
                <label className={styles.inline}>
                  <input
                    type="checkbox"
                    checked={rightHasSymptom("ulcus abgeheilt")}
                    onChange={(e) => toggleUlcusAbgeheilt("right", e.target.checked)}
                  /> abgeheilt
                </label>
                <label className={styles.inline}>
                  <input
                    type="checkbox"
                    checked={rightHasSymptom("ulcus akut")}
                    onChange={(e) => toggleUlcusAkut("right", e.target.checked)}
                    disabled={!rightHasSymptom("pnp")}
                  /> Ulcus akut
                </label>
                {rightHasSymptom("ulcus akut") && (
                  <div className={styles.checkboxes} style={{ marginLeft: 12 }}>
                    {/* 1) Location type */}
                    <div className={styles.small}>1 auswählen</div>
                    <div className={styles.radios}>
                      <label>
                        <input
                          type="checkbox"
                          checked={rightHasCriteria("Nicht plantare Laesion")}
                          onChange={(e) =>
                            chooseUlcusLocationType("right", e.target.checked ? "Nicht plantare Laesion" : "")
                          }
                        />
                        Nicht plantare Läsionen
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={rightHasCriteria("ulcus mit plantarer Laesion")}
                          onChange={(e) =>
                            chooseUlcusLocationType("right", e.target.checked ? "ulcus mit plantarer Laesion" : "")
                          }
                        />
                        Ulcus mit plantarer Läsion
                      </label>
                    </div>
                    {/* 2) Heel involvement */}
                    <div className={styles.small} style={{ marginTop: 8 }}>1 auswählen</div>
                    <div className={styles.radios}>
                      <label>
                        <input
                          type="checkbox"
                          checked={rightHasSymptom("ausser Ferse")}
                          onChange={(e) =>
                            chooseUlcusHeel("right", e.target.checked ? "ausser Ferse" : "")
                          }
                        />
                        außer Ferse
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={rightHasSymptom("Ferse")}
                          onChange={(e) =>
                            chooseUlcusHeel("right", e.target.checked ? "Ferse" : "")
                          }
                        />
                        Ferse
                      </label>
                    </div>
                    {/* 3) Infection / Exudation */}
                    <div className={styles.small} style={{ marginTop: 8 }}>1 auswählen</div>
                    <div className={styles.radios}>
                      <label>
                        <input
                          type="checkbox"
                          checked={rightHasCriteria("milde Infektion")}
                          onChange={(e) =>
                            chooseUlcusInfection("right", e.target.checked ? "milde Infektion" : "")
                          }
                        />
                        Ulcus milde Infektion
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={rightHasCriteria("moderate Infektion")}
                          onChange={(e) =>
                            chooseUlcusInfection("right", e.target.checked ? "moderate Infektion" : "")
                          }
                        />
                        Ulcus moderate Infektion
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={rightHasCriteria("starke Infektion")}
                          onChange={(e) =>
                            chooseUlcusInfection("right", e.target.checked ? "starke Infektion" : "")
                          }
                        />
                        Ulcus starke Infektion
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={rightHasCriteria("starke Exsudation")}
                          onChange={(e) =>
                            chooseUlcusInfection("right", e.target.checked ? "starke Exsudation" : "")
                          }
                        />
                        Ulcus starke Exsudation
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </td>
          </tr>

          {/* 4) Praeulzerative */}
          <tr>
            <td className={styles.clinical}>4) Hat der/die Patient*in präulzerative Veränderungen (z.B. eingeblutete Hyperkeratosen)?</td>
            <td className={styles.cell}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>linker Fuß</div>
              <label className={styles.inline}>
                <input
                  type="checkbox"
                  checked={leftHasCriteria("praeulzerative Veraenderungen")}
                  onChange={(e) => toggleCriteria("left", "praeulzerative Veraenderungen", e.target.checked)}
                /> Ja
              </label>
            </td>
            <td className={styles.cell}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>rechter Fuß</div>
              <label className={styles.inline}>
                <input
                  type="checkbox"
                  checked={rightHasCriteria("praeulzerative Veraenderungen")}
                  onChange={(e) => toggleCriteria("right", "praeulzerative Veraenderungen", e.target.checked)}
                /> Ja
              </label>
            </td>
          </tr>

          {/* 5) Krallen/Hammerzehen */}
          <tr>
            <td className={styles.clinical}>5) Drohende dorsale Ulcera / ausgeprägte Krallen- oder Hammerzehen?</td>
            <td className={styles.cell}>
              <label className={styles.inline}>
                <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>linker Fuß</div>
                <input
                  type="checkbox"
                  checked={leftHasCriteria("ausgepraegte Krallen- oder Hammerzehe")}
                  onChange={(e) => toggleCriteria("left", "ausgepraegte Krallen- oder Hammerzehe", e.target.checked)}
                /> Ja
              </label>
            </td>
            <td className={styles.cell}>
              <label className={styles.inline}>
                <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>rechter Fuß</div>
                <input
                  type="checkbox"
                  checked={rightHasCriteria("ausgepraegte Krallen- oder Hammerzehe")}
                  onChange={(e) => toggleCriteria("right", "ausgepraegte Krallen- oder Hammerzehe", e.target.checked)}
                /> Ja
              </label>
            </td>
          </tr>

          {/* 6) Deformitäten */}
          <tr>
            <td className={styles.clinical}>
              6) Hat der/die Patient*in{" "}
              <span className={styles["hover-term"]}>
                Deformitäten
                <span className={styles.tooltip}>
                  <strong>Fußdeformität</strong><br /><br />
                  Strukturelle Anomalien des Fußes wie Hammerzehen, Krallenzehen,
                  Hallux valgus, prominente Mittelfußknochenköpfe, Pes cavus (Hohlfuß),
                  Pes planus (Plattfuß) und Folgen von Charcot-Neuroosteoarthropathie,
                  Traumata, Amputationen oder anderen chirurgischen Eingriffen am Fuß.<br /><br />
                  <em>Sybille Wunderlich. IWGDF Guidelines (2019), S.203</em>
                </span>
              </span>
              ?
            </td>
            <td className={styles.cell}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>linker Fuß</div>
              <label className={styles.inline}>
                <input
                  type="checkbox"
                  checked={leftHasSymptom("Deformitaet")}
                  onChange={(e) => toggleDeformTop("left", e.target.checked)}
                /> Ja
              </label>
              {leftHasSymptom("Deformitaet") && (
                <div className={styles.checkboxes} style={{ marginLeft: 12 }}>
                  <div className={styles.small}>1 auswählen</div>
                  <div className={styles["exclusive-group"]}>
                    <label>
                      <input
                        type="checkbox"
                        checked={leftHasCriteria("ausgepraegte Hohlfuss")}
                        onChange={(e) => chooseExclusiveDeformity("left", "ausgepraegte Hohlfuss", e.target.checked)}
                      /> Deformität ausgeprägte Hohlfuß
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={leftHasCriteria("ausgepraegte Knick/Senkfuss")}
                        onChange={(e) => chooseExclusiveDeformity("left", "ausgepraegte Knick/Senkfuss", e.target.checked)}
                      /> Deformität Knick / Senkfuß
                    </label>
                  </div>
                  <label>
                    <input
                      type="checkbox"
                      checked={leftHasCriteria("ausgepraegte Zehenfehlstellung")}
                      onChange={(e) => toggleCriteria("left", "ausgepraegte Zehenfehlstellung", e.target.checked)}
                    /> Deformität Zehenfehlstellung
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={leftHasCriteria("ausgepraegte Dyspropertion, Deformitaeten bzw. Dysproportionen, nicht nach konfektioniertem Leisten")}
                      onChange={(e) => toggleCriteria("left", "ausgepraegte Dyspropertion, Deformitaeten bzw. Dysproportionen, nicht nach konfektioniertem Leisten", e.target.checked)}
                    /> ausgeprägte Dyspropertion nicht nach konfektioniertem Leisten (z. B. Spezialschuh bei DFS) zu versorgen
                  </label>
                </div>
              )}
            </td>
            <td className={styles.cell}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>rechter Fuß</div>
              <label className={styles.inline}>
                <input
                  type="checkbox"
                  checked={rightHasSymptom("Deformitaet")}
                  onChange={(e) => toggleDeformTop("right", e.target.checked)}
                /> Ja
              </label>
              {rightHasSymptom("Deformitaet") && (
                <div className={styles.checkboxes} style={{ marginLeft: 12 }}>
                  <div className={styles.small}>1 auswählen</div>
                  <div className={styles["exclusive-group"]}>
                    <label>
                      <input
                        type="checkbox"
                        checked={rightHasCriteria("ausgepraegte Hohlfuss")}
                        onChange={(e) => chooseExclusiveDeformity("right", "ausgepraegte Hohlfuss", e.target.checked)}
                      /> Deformität ausgeprägte Hohlfuß
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={rightHasCriteria("ausgepraegte Knick/Senkfuss")}
                        onChange={(e) => chooseExclusiveDeformity("right", "ausgepraegte Knick/Senkfuss", e.target.checked)}
                      /> Deformität Knick / Senkfuß
                    </label>
                  </div>
                  <label>
                    <input
                      type="checkbox"
                      checked={rightHasCriteria("ausgepraegte Zehenfehlstellung")}
                      onChange={(e) => toggleCriteria("right", "ausgepraegte Zehenfehlstellung", e.target.checked)}
                    /> Deformität Zehenfehlstellung
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={rightHasCriteria("ausgepraegte Dyspropertion, Deformitaeten bzw. Dysproportionen, nicht nach konfektioniertem Leisten")}
                      onChange={(e) => toggleCriteria("right", "ausgepraegte Dyspropertion, Deformitaeten bzw. Dysproportionen, nicht nach konfektioniertem Leisten", e.target.checked)}
                    /> ausgeprägte Dyspropertion nicht nach konfektioniertem Leisten (z. B. Spezialschuh bei DFS) zu versorgen
                  </label>
                </div>
              )}
            </td>
          </tr>

          {/* 7) Amputationen */}
          <tr>
            <td className={styles.clinical}>7) Hat der/die Patient*in Amputationen?</td>
            {/* Linker Fuß */}
            <td className={styles.cell}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>Linker Fuß</div>
              <label className={styles.inline}>
                <input
                  type="checkbox"
                  checked={left.symptoms.includes("Amputation")}
                  onChange={(e) => toggleAmputationTop("left", e.target.checked)}
                /> Ja
              </label>
              {left.symptoms.includes("Amputation") && (
                <>
                  <div style={{ marginLeft: 12, marginTop: 6 }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={left.symptoms.includes("Zeh Amputation")}
                        onChange={(e) => toggleZehAmputation("left", e.target.checked)}
                        disabled={
                          left.criteria.includes("Teil Amputation Mittelfuss") ||
                          left.criteria.includes("Teil Amputation Rueckfuss")
                        }
                      /> Zeh Amputation
                    </label>
                    {left.symptoms.includes("Zeh Amputation") && (
                      <div className={styles.checkboxes} style={{ marginLeft: 12 }}>
                        {["D1", "D2", "D3", "D4", "D5"].map((d) => (
                          <label key={d}>
                            <input
                              type="checkbox"
                              checked={left.criteria.includes(`Zeh ${d} Amputation`)}
                              onChange={(e) => toggleZehDigit("left", d, e.target.checked)}
                            /> Zeh {d}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ marginLeft: 12, marginTop: 10 }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={left.symptoms.includes("Teil Amputation")}
                        onChange={(e) => toggleTeilAmputationTop("left", e.target.checked)}
                      /> Teil Amputationen
                    </label>
                    {left.symptoms.includes("Teil Amputation") && (
                      <div style={{ marginLeft: 12 }}>
                        <div className={styles.small}>1 auswählen</div>
                        <div className={styles.radios} style={{ marginTop: 6 }}>
                          <label>
                            <input
                              type="checkbox"
                              checked={leftHasCriteria("Transmetatarsale Teilamputation des Vorfusses")}
                              onChange={(e) =>
                                chooseTeilGroupA(
                                  "left",
                                  e.target.checked
                                    ? "Transmetatarsale Teilamputation des Vorfusses"
                                    : ""
                                )
                              }
                            /> Vorfuß
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={leftHasCriteria("Teil Amputation Mittelfuss")}
                              onChange={(e) =>
                                chooseTeilGroupA(
                                  "left",
                                  e.target.checked ? "Teil Amputation Mittelfuss" : ""
                                )
                              }
                            /> Mittelfuß
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={leftHasCriteria("Teil Amputation Rueckfuss")}
                              onChange={(e) =>
                                chooseTeilGroupA(
                                  "left",
                                  e.target.checked ? "Teil Amputation Rueckfuss" : ""
                                )
                              }
                            /> Rückfuß
                          </label>
                        </div>
                        <div className={styles.small} style={{ marginTop: 8 }}>1 auswählen</div>
                        <div className={styles.radios} style={{ marginTop: 6 }}>
                          <label>
                            <input
                              type="checkbox"
                              checked={leftHasCriteria("belaster Fussstumpf")}
                              onChange={(e) =>
                                chooseTeilGroupB("left", e.target.checked ? "belaster Fussstumpf" : "")
                              }
                            /> belaster Fußstumpf
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={leftHasCriteria("nicht belaster Fussstumpf")}
                              onChange={(e) =>
                                chooseTeilGroupB("left", e.target.checked ? "nicht belaster Fussstumpf" : "")
                              }
                            /> nicht belaster Fußstumpf
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </td>

            {/* Rechter Fuß */}
            <td className={styles.cell}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>Rechter Fuß</div>
              <label className={styles.inline}>
                <input
                  type="checkbox"
                  checked={right.symptoms.includes("Amputation")}
                  onChange={(e) => toggleAmputationTop("right", e.target.checked)}
                /> Ja
              </label>
              {right.symptoms.includes("Amputation") && (
                <>
                  <div style={{ marginLeft: 12, marginTop: 6 }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={right.symptoms.includes("Zeh Amputation")}
                        onChange={(e) => toggleZehAmputation("right", e.target.checked)}
                        disabled={
                          right.criteria.includes("Teil Amputation Mittelfuss") ||
                          right.criteria.includes("Teil Amputation Rueckfuss")
                        }
                      /> Zeh Amputation
                    </label>
                    {right.symptoms.includes("Zeh Amputation") && (
                      <div className={styles.checkboxes} style={{ marginLeft: 12 }}>
                        {["D1", "D2", "D3", "D4", "D5"].map((d) => (
                          <label key={d}>
                            <input
                              type="checkbox"
                              checked={right.criteria.includes(`Zeh ${d} Amputation`)}
                              onChange={(e) => toggleZehDigit("right", d, e.target.checked)}
                            /> Zeh {d}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ marginLeft: 12, marginTop: 10 }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={right.symptoms.includes("Teil Amputation")}
                        onChange={(e) => toggleTeilAmputationTop("right", e.target.checked)}
                      /> Teil Amputationen
                    </label>
                    {right.symptoms.includes("Teil Amputation") && (
                      <div style={{ marginLeft: 12 }}>
                        <div className={styles.small}>1 auswählen</div>
                        <div className={styles.radios} style={{ marginTop: 6 }}>
                          <label>
                            <input
                              type="checkbox"
                              checked={rightHasCriteria("Transmetatarsale Teilamputation des Vorfusses")}
                              onChange={(e) =>
                                chooseTeilGroupA(
                                  "right",
                                  e.target.checked
                                    ? "Transmetatarsale Teilamputation des Vorfusses"
                                    : ""
                                )
                              }
                            /> Vorfuß
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={rightHasCriteria("Teil Amputation Mittelfuss")}
                              onChange={(e) =>
                                chooseTeilGroupA("right", e.target.checked ? "Teil Amputation Mittelfuss" : "")
                              }
                            /> Mittelfuß
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={rightHasCriteria("Teil Amputation Rueckfuss")}
                              onChange={(e) =>
                                chooseTeilGroupA("right", e.target.checked ? "Teil Amputation Rueckfuss" : "")
                              }
                            /> Rückfuß
                          </label>
                        </div>
                        <div className={styles.small} style={{ marginTop: 8 }}>1 auswählen</div>
                        <div className={styles.radios} style={{ marginTop: 6 }}>
                          <label>
                            <input
                              type="checkbox"
                              checked={rightHasCriteria("belaster Fussstumpf")}
                              onChange={(e) =>
                                chooseTeilGroupB("right", e.target.checked ? "belaster Fussstumpf" : "")
                              }
                            /> belaster Fußstumpf
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={rightHasCriteria("nicht belaster Fussstumpf")}
                              onChange={(e) =>
                                chooseTeilGroupB("right", e.target.checked ? "nicht belaster Fussstumpf" : "")
                              }
                            /> nicht belaster Fußstumpf
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </td>
          </tr>

          {/* 8) CNO */}
          <tr>
            <td className={styles.clinical}>
              8) Hat der/die Patient*in Hinweise auf eine{" "}
              <span className={styles["hover-term"]}>
                Charcot-Neuro-Osteoarthropathie (CNO)
                <span className={styles.tooltip}>
                  <strong>CNO</strong><br /><br />
                  Neuroosteoarthropathie (Charcot-Fuß): Nicht-infektiöse Zerstörung von
                  Knochen und Gelenken in Verbindung mit Neuropathie, die in der akuten
                  Phase von Entzündungszeichen begleitet wird.<br /><br />
                  <em>Sybille Wunderlich. IWGDF Guidelines (2019), S.203</em>
                </span>
              </span>
              ?
            </td>
            <td className={styles.cell}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>linker Fuß</div>
              <label className={styles.inline}>
                <input
                  type="checkbox"
                  checked={left.symptoms.includes("CNO")}
                  onChange={(e) => toggleCnoTop("left", e.target.checked)}
                  disabled={!leftHasSymptom("pnp")}
                /> Ja
              </label>
              {left.symptoms.includes("CNO") && (
                <div style={{ marginLeft: 12 }}>
                  <div style={{ marginTop: 6 }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={left.symptoms.includes("CNO inaktiv")}
                        onChange={(e) => toggleCnoMode("left", "inaktiv", e.target.checked)}
                      /> CNO inaktiv
                    </label>
                    {left.symptoms.includes("CNO inaktiv") && (
                      <div style={{ marginLeft: 12, marginTop: 6 }}>
                        <div className={styles.small}>Lokalisation (1 auswählen)</div>
                        <div className={styles.radios}>
                          <label>
                            <input
                              type="checkbox"
                              checked={leftHasCriteria("Vorfuss")}
                              onChange={(e) => chooseCnoLocation("left", "inaktiv", e.target.checked ? "Vorfuss" : "")}
                            /> Vorfuß
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={leftHasCriteria("Mittelfuss")}
                              onChange={(e) => chooseCnoLocation("left", "inaktiv", e.target.checked ? "Mittelfuss" : "")}
                            /> Mittelfuß
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={leftHasCriteria("Rueckfuss")}
                              onChange={(e) => chooseCnoLocation("left", "inaktiv", e.target.checked ? "Rueckfuss" : "")}
                            /> Rückfuß
                          </label>
                        </div>
                        <div className={styles.small} style={{ marginTop: 6 }}>Deformstatus (1 auswählen)</div>
                        <div className={styles.radios}>
                          <label>
                            <input
                              type="checkbox"
                              checked={leftHasCriteria("minderdeformiert mit erhaltener Belastbarkeit")}
                              onChange={(e) => chooseCnoDeform("left", "inaktiv", e.target.checked ? "minderdeformiert mit erhaltener Belastbarkeit" : "")}
                            /> minderdeformiert mit erhaltener Belastbarkeit
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={leftHasCriteria("mit Derformation")}
                              onChange={(e) => chooseCnoDeform("left", "inaktiv", e.target.checked ? "mit Derformation" : "")}
                            /> mit Derformation
                          </label>
                        </div>
                        <div className={styles.checkboxes} style={{ marginTop: 6 }}>
                          <label>
                            <input
                              type="checkbox"
                              checked={leftHasCriteria("Gelenkdestruktion USG / OSG starke Lotabweichung")}
                              onChange={(e) =>
                                toggleCnoExtra("left", "inaktiv", "destr", e.target.checked)
                              }
                            />
                            Gelenkdestruktion USG / OSG starke Lotabweichung
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={leftHasCriteria("Mobilitaetsgrad 1")}
                              onChange={(e) =>
                                toggleCnoExtra("left", "inaktiv", "mobi", e.target.checked)
                              }
                            />
                            Mobilitätsgrad 1
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={left.symptoms.includes("CNO aktiv")}
                        onChange={(e) => toggleCnoMode("left", "aktiv", e.target.checked)}
                      /> CNO aktiv
                    </label>
                    {left.symptoms.includes("CNO aktiv") && (
                      <div style={{ marginLeft: 12, marginTop: 6 }}>
                        <div className={styles.small}>Lokalisation (1 auswählen)</div>
                        <div className={styles.radios}>
                          <label>
                            <input
                              type="checkbox"
                              checked={leftHasCriteria("Vorfuss")}
                              onChange={(e) => chooseCnoLocation("left", "aktiv", e.target.checked ? "Vorfuss" : "")}
                            /> Vorfuß
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={leftHasCriteria("Mittelfuss")}
                              onChange={(e) => chooseCnoLocation("left", "aktiv", e.target.checked ? "Mittelfuss" : "")}
                            /> Mittelfuß
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={leftHasCriteria("Rueckfuss")}
                              onChange={(e) => chooseCnoLocation("left", "aktiv", e.target.checked ? "Rueckfuss" : "")}
                            /> Rückfuß
                          </label>
                        </div>
                        <div className={styles.small} style={{ marginTop: 6 }}>Deformstatus (1 auswählen)</div>
                        <div className={styles.radios}>
                          <label>
                            <input
                              type="checkbox"
                              checked={leftHasCriteria("minderdeformiert mit erhaltener Belastbarkeit")}
                              onChange={(e) => chooseCnoDeform("left", "aktiv", e.target.checked ? "minderdeformiert mit erhaltener Belastbarkeit" : "")}
                            /> minderdeformiert mit erhaltener Belastbarkeit
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={leftHasCriteria("mit Derformation")}
                              onChange={(e) => chooseCnoDeform("left", "aktiv", e.target.checked ? "mit Derformation" : "")}
                            /> mit Derformation
                          </label>
                        </div>
                        <div className={styles.checkboxes} style={{ marginTop: 6 }}>
                          <label>
                            <input
                              type="checkbox"
                              checked={leftHasCriteria("Gelenkdestruktion USG / OSG starke Lotabweichung")}
                              onChange={(e) =>
                                toggleCnoExtra("left", "aktiv", "destr", e.target.checked)
                              }
                            />
                            Gelenkdestruktion USG / OSG starke Lotabweichung
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={leftHasCriteria("Mobilitaetsgrad 1")}
                              onChange={(e) =>
                                toggleCnoExtra("left", "aktiv", "mobi", e.target.checked)
                              }
                            />
                            Mobilitätsgrad 1
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </td>
            <td className={styles.cell}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>rechter Fuß</div>
              <label className={styles.inline}>
                <input
                  type="checkbox"
                  checked={right.symptoms.includes("CNO")}
                  onChange={(e) => toggleCnoTop("right", e.target.checked)}
                  disabled={!rightHasSymptom("pnp")}
                /> Ja
              </label>
              {right.symptoms.includes("CNO") && (
                <div style={{ marginLeft: 12 }}>
                  <div style={{ marginTop: 6 }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={right.symptoms.includes("CNO inaktiv")}
                        onChange={(e) => toggleCnoMode("right", "inaktiv", e.target.checked)}
                      /> CNO inaktiv
                    </label>
                    {right.symptoms.includes("CNO inaktiv") && (
                      <div style={{ marginLeft: 12, marginTop: 6 }}>
                        <div className={styles.small}>Lokalisation (1 auswählen)</div>
                        <div className={styles.radios}>
                          <label>
                            <input
                              type="checkbox"
                              checked={rightHasCriteria("Vorfuss")}
                              onChange={(e) => chooseCnoLocation("right", "inaktiv", e.target.checked ? "Vorfuss" : "")}
                            /> Vorfuß
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={rightHasCriteria("Mittelfuss")}
                              onChange={(e) => chooseCnoLocation("right", "inaktiv", e.target.checked ? "Mittelfuss" : "")}
                            /> Mittelfuß
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={rightHasCriteria("Rueckfuss")}
                              onChange={(e) => chooseCnoLocation("right", "inaktiv", e.target.checked ? "Rueckfuss" : "")}
                            /> Rückfuß
                          </label>
                        </div>
                        <div className={styles.small} style={{ marginTop: 6 }}>Deformstatus (1 auswählen)</div>
                        <div className={styles.radios}>
                          <label>
                            <input
                              type="checkbox"
                              checked={rightHasCriteria("minderdeformiert mit erhaltener Belastbarkeit")}
                              onChange={(e) => chooseCnoDeform("right", "inaktiv", e.target.checked ? "minderdeformiert mit erhaltener Belastbarkeit" : "")}
                            /> minderdeformiert mit erhaltener Belastbarkeit
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={rightHasCriteria("mit Derformation")}
                              onChange={(e) => chooseCnoDeform("right", "inaktiv", e.target.checked ? "mit Derformation" : "")}
                            /> mit Derformation
                          </label>
                        </div>
                        <div className={styles.checkboxes} style={{ marginTop: 6 }}>
                          <label>
                            <input
                              type="checkbox"
                              checked={rightHasCriteria("Gelenkdestruktion USG / OSG starke Lotabweichung")}
                              onChange={(e) =>
                                toggleCnoExtra("right", "inaktiv", "destr", e.target.checked)
                              }
                            />
                            Gelenkdestruktion USG / OSG starke Lotabweichung
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={rightHasCriteria("Mobilitaetsgrad 1")}
                              onChange={(e) =>
                                toggleCnoExtra("right", "inaktiv", "mobi", e.target.checked)
                              }
                            />
                            Mobilitätsgrad 1
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={right.symptoms.includes("CNO aktiv")}
                        onChange={(e) => toggleCnoMode("right", "aktiv", e.target.checked)}
                      /> CNO aktiv
                    </label>
                    {right.symptoms.includes("CNO aktiv") && (
                      <div style={{ marginLeft: 12, marginTop: 6 }}>
                        <div className={styles.small}>Lokalisation (1 auswählen)</div>
                        <div className={styles.radios}>
                          <label>
                            <input
                              type="checkbox"
                              checked={rightHasCriteria("Vorfuss")}
                              onChange={(e) => chooseCnoLocation("right", "aktiv", e.target.checked ? "Vorfuss" : "")}
                            /> Vorfuß
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={rightHasCriteria("Mittelfuss")}
                              onChange={(e) => chooseCnoLocation("right", "aktiv", e.target.checked ? "Mittelfuss" : "")}
                            /> Mittelfuß
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={rightHasCriteria("Rueckfuss")}
                              onChange={(e) => chooseCnoLocation("right", "aktiv", e.target.checked ? "Rueckfuss" : "")}
                            /> Rückfuß
                          </label>
                        </div>
                        <div className={styles.small} style={{ marginTop: 6 }}>Deformstatus (1 auswählen)</div>
                        <div className={styles.radios}>
                          <label>
                            <input
                              type="checkbox"
                              checked={rightHasCriteria("minderdeformiert mit erhaltener Belastbarkeit")}
                              onChange={(e) => chooseCnoDeform("right", "aktiv", e.target.checked ? "minderdeformiert mit erhaltener Belastbarkeit" : "")}
                            /> minderdeformiert mit erhaltener Belastbarkeit
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={rightHasCriteria("mit Derformation")}
                              onChange={(e) => chooseCnoDeform("right", "aktiv", e.target.checked ? "mit Derformation" : "")}
                            /> mit Derformation
                          </label>
                        </div>
                        <div className={styles.checkboxes} style={{ marginTop: 6 }}>
                          <label>
                            <input
                              type="checkbox"
                              checked={rightHasCriteria("Gelenkdestruktion USG / OSG starke Lotabweichung")}
                              onChange={(e) =>
                                toggleCnoExtra("right", "aktiv", "destr", e.target.checked)
                              }
                            />
                            Gelenkdestruktion USG / OSG starke Lotabweichung
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={rightHasCriteria("Mobilitaetsgrad 1")}
                              onChange={(e) =>
                                toggleCnoExtra("right", "aktiv", "mobi", e.target.checked)
                              }
                            />
                            Mobilitätsgrad 1
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </td>
          </tr>

          {/* 9) Dialysepflichtig */}
          <tr>
            <td className={styles.clinical}>9) Ist der/die Patient*in dialysepflichtig?</td>
            <td className={styles.cell}>
              <label className={styles.inline}>
                <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>linker Fuß</div>
                <input
                  type="checkbox"
                  checked={leftHasCriteria("dialysepflichtig")}
                  onChange={(e) => toggleDialyse("left", e.target.checked)}
                /> Ja
              </label>
            </td>
            <td className={styles.cell}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>rechter Fuß</div>
              <label className={styles.inline}>
                <input
                  type="checkbox"
                  checked={rightHasCriteria("dialysepflichtig")}
                  onChange={(e) => toggleDialyse("right", e.target.checked)}
                /> Ja
              </label>
            </td>
          </tr>
        </tbody>
      </table>
<div className={styles["form-actions"]}>
  <button
    type="button"                         
    className={styles["submit-btn"]}       
    onClick={() => navigate("/")}          
  >
    Zurück
  </button>

  <button
    className={styles["submit-btn"]}
    type="submit"
    disabled={loading}
  >
    {loading ? "Bitte warten..." : "Bewertung erstellen"}
  </button>
</div>
    </form>
  </div>
);
}
